import React from "react";
import {
  Alert,
  FormControlLabel,
  Stack,
  Switch,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import UploadArea from "./UploadArea";
import PromptField from "./PromptField";
import DurationAspectRow from "./DurationAspectRow";
import VoiceoverSection from "./VoiceoverSection";
import MusicAudioSection from "./MusicAudioSection";
import ScenePlanEditor from "./ScenePlanEditor";
import {
  ASPECT_RATIO_OPTIONS,
  GROK_ASPECT_RATIO_OPTIONS,
  GROK_MODE_OPTIONS,
  GROK_VIDEO_15_RESOLUTIONS,
} from "../categories";
import { getGenerationMode, getGenerationModeRules } from "../studioGenerationMode";
import { formHasScenePlan } from "../scenePlan";
import { getModelFamily } from "../modelProfiles";

export default function GenericModelFormPanel({
  form,
  onChange,
  lang,
  categoryId,
  modelId,
  onGenerateScene,
  generatingScenes,
  sceneStatuses,
}) {
  const set = (patch) => onChange({ ...form, ...patch });
  const generationMode = getGenerationMode(categoryId);
  const rules = getGenerationModeRules(generationMode);
  const mid = modelId || form.model;
  const family = getModelFamily(mid);
  const isGrok15 = String(mid || "").includes("grok-imagine-video");
  const isGrok = family === "grok";
  const aspectOptions = isGrok && !isGrok15 ? GROK_ASPECT_RATIO_OPTIONS : ASPECT_RATIO_OPTIONS;
  const maxImages =
    generationMode === "video-to-video"
      ? 1
      : family === "omni"
        ? 3
        : isGrok
          ? 1
          : family === "seedance"
            ? 2
            : 2;

  return (
    <Stack spacing={2}>
      {(family === "omni" || family === "seedance" || isGrok) && (
        <Alert severity="info" sx={{ py: 0.5 }}>
          <Typography variant="caption">
            {family === "omni"
              ? lang?.videoStudioOmniFlashHint ||
                "Omni Flash (Google): text-only, 1 image, or 3 images for reference fusion. Optional video reference. Durations 4–10s."
              : family === "seedance"
                ? lang?.videoStudioSeedanceHint ||
                  "Seedance 2: multimodal text/image/video/audio with native sound. Clips 4–15s (longer targets auto-stitch)."
                : isGrok15
                  ? lang?.videoStudioGrok15Hint ||
                    "Grok Imagine Video 1.5: image-to-video only. Upload one image. Duration 1–15s, 480p or 720p."
                  : lang?.videoStudioGrokHint ||
                    "Grok Imagine: text-to-video or one image. Duration 6s or 10s. Modes: fun, normal, spicy."}
          </Typography>
        </Alert>
      )}

      {/* Uploads first so @-mentions have assets available while typing the prompt. */}
      {rules.showImages && (
        <Stack spacing={0.75}>
          <UploadArea
            lang={lang}
            enableVoiceClip
            label={
              generationMode === "video-to-video"
                ? lang?.videoStudioCharacterImage || "Character / avatar"
                : family === "omni"
                  ? lang?.videoStudioOmniImages || "Images (0, 1, or 3)"
                  : isGrok15
                    ? lang?.videoStudioGrokSourceImage || "Source image (required)"
                    : isGrok
                      ? lang?.videoStudioGrokImage || "Image (optional)"
                      : lang?.videoStudioInputImage || "Input image"
            }
            hint={
              generationMode === "video-to-video"
                ? lang?.videoStudioCharacterImageHint ||
                  "Portrait photo — tap the mic below to attach this character’s voice"
                : family === "omni"
                  ? lang?.videoStudioOmniImagesHint ||
                    "None for text-to-video, 1 for image-to-video, or 3 for reference fusion. Tap mic under each image to attach voice."
                  : isGrok
                    ? lang?.videoStudioGrokImageHint ||
                      "JPEG/PNG — tap the mic to set this character’s voice; Dialogue lines are spoken in that voice"
                    : lang?.videoStudioInputImageHint ||
                      "JPEG, PNG, or WebP — we auto-describe each image so @-tags keep that likeness. Tap mic to attach voice."
            }
            files={form.imageUrlUploads || []}
            onChange={(imageUrlUploads) => set({ imageUrlUploads })}
            maxFiles={maxImages}
          />
          {family === "omni" &&
          (form.imageUrlUploads || []).filter((f) => f.file || f.previewUrl).length === 2 ? (
            <Alert severity="warning" sx={{ py: 0.25 }}>
              <Typography variant="caption">
                {lang?.videoStudioOmniTwoImagesWarn ||
                  "Omni Flash does not accept 2 images. Remove one (use 1) or add a third (use 3)."}
              </Typography>
            </Alert>
          ) : null}
        </Stack>
      )}

      {rules.showReferenceImages && (
        <UploadArea
          lang={lang}
          label={lang?.videoStudioProductRefImage || "Product reference"}
          hint={
            lang?.videoStudioProductRefImageHint ||
            "Packshot or label — guides the product shown in the ad (JPEG/PNG, max 10MB)"
          }
          files={form.referenceImageUploads || []}
          onChange={(referenceImageUploads) => set({ referenceImageUploads })}
          maxFiles={2}
        />
      )}

      {rules.showVideos && (
        <UploadArea
          lang={lang}
          kind="video"
          accept="video/mp4,video/webm,video/quicktime"
          maxBytes={50 * 1024 * 1024}
          label={lang?.videoStudioInputVideo || "Reference video"}
          hint={lang?.videoStudioInputVideoHint || "MP4, WebM, or MOV — max 50 MB"}
          files={form.referenceVideoUploads || []}
          onChange={(referenceVideoUploads) => set({ referenceVideoUploads })}
          maxFiles={1}
        />
      )}

      {rules.showPrompt && (
        <PromptField
          form={form}
          onChange={onChange}
          lang={lang}
          textFieldProps={{ minRows: 4 }}
        />
      )}

      {rules.showMusic && <MusicAudioSection form={form} onChange={onChange} lang={lang} />}

      {rules.showVoiceover && (
        <VoiceoverSection form={form} onChange={onChange} lang={lang} modelId={modelId || form.model} />
      )}

      {isGrok && !isGrok15 ? (
        <Stack spacing={0.5}>
          <Typography variant="caption" color="text.secondary" fontWeight={600}>
            {lang?.videoStudioGrokMode || "Style mode"}
          </Typography>
          <ToggleButtonGroup
            exclusive
            size="small"
            value={form.grokMode || "normal"}
            onChange={(_, v) => v && set({ grokMode: v })}
          >
            {GROK_MODE_OPTIONS.map((m) => (
              <ToggleButton key={m} value={m} sx={{ textTransform: "capitalize", minWidth: 64 }}>
                {m}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
        </Stack>
      ) : null}

      {isGrok15 ? (
        <Stack spacing={0.5}>
          <Typography variant="caption" color="text.secondary" fontWeight={600}>
            {lang?.videoStudioResolution || "Resolution"}
          </Typography>
          <ToggleButtonGroup
            exclusive
            size="small"
            value={GROK_VIDEO_15_RESOLUTIONS.includes(form.resolution) ? form.resolution : "720p"}
            onChange={(_, v) => v && set({ resolution: v })}
          >
            {GROK_VIDEO_15_RESOLUTIONS.map((r) => (
              <ToggleButton key={r} value={r} sx={{ minWidth: 64 }}>
                {r}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
        </Stack>
      ) : null}

      <DurationAspectRow
        form={form}
        onChange={onChange}
        lang={lang}
        modelId={modelId || form.model}
        aspectOptions={aspectOptions}
        hideAspect={isGrok15}
      />

      {formHasScenePlan(form) ? (
        <ScenePlanEditor
          form={form}
          onChange={onChange}
          lang={lang}
          onGenerateScene={onGenerateScene}
          generatingScenes={generatingScenes}
          sceneStatuses={sceneStatuses}
        />
      ) : null}

      {!rules.showMusic && !isGrok && (
        <FormControlLabel
          control={
            <Switch
              checked={form.sound !== false}
              onChange={(e) => set({ sound: e.target.checked })}
            />
          }
          label={lang?.videoStudioSound || "Sound (native voiceover)"}
        />
      )}
    </Stack>
  );
}
